import { CompanyPermissions } from "@/app/api/types/superadmin";
import type { PreOrder } from "@/app/api/types/preOrder";

/**
 * Frontend mirror of the V2 API pre-order mutate gate.
 *
 * The server resolves write authority with `buildPreOrderVisibility`:
 *
 *   isManager
 *     OR allConfirmedVisible (`recap_show_all_confirmed` company flag)
 *     OR preOrder.user_id  === viewer.userId
 *     OR preOrder.group_id ∈   viewer.groupIds
 *
 * The flag widens both read AND mutate authority — if a viewer can SEE a
 * foreign row exposed by Mode B, they can also accept/decline/modify it.
 * (Previously mutate pinned `allConfirmedVisible: false`; that was the
 * source of "I can see this pending order but the Edit button is hidden".)
 *
 * This helper is advisory UX only: the V2 approve/decline + push-batch
 * endpoints enforce the same gate server-side (403 `ForbiddenSyncWriteError`).
 */
export interface PreOrderMutateViewer {
  userId: string;
  /** `user.company_permissions` — null for users without a company role. */
  companyPermissions: number | null;
  /** Ids of every group the viewer owns or is a member of (`useMyGroups`). */
  groupIds: string[];
  /** `recap_show_all_confirmed` company flag — when true, mutate authority
   *  follows read authority (i.e. anyone in the company). */
  allConfirmedVisible: boolean;
}

/** SUPER_ADMIN (0) / OWNER (1) — same `<= OWNER` test as `roleLookups.isManager`. */
export function isManagerPermission(
  companyPermissions: number | null | undefined,
): boolean {
  return (
    companyPermissions != null && companyPermissions <= CompanyPermissions.OWNER
  );
}

export function canMutatePreOrder(
  preOrder: Pick<PreOrder, "user_id" | "group_id">,
  viewer: PreOrderMutateViewer,
): boolean {
  if (isManagerPermission(viewer.companyPermissions)) return true;
  if (viewer.allConfirmedVisible) return true;
  if (preOrder.user_id === viewer.userId) return true;
  return (
    preOrder.group_id != null && viewer.groupIds.includes(preOrder.group_id)
  );
}
